import { useState } from 'react';
import { Link } from 'react-router-dom';

export interface GettingStartedStep {
  id: string;
  title: string;
  description: string;
  to: string;
  complete: boolean;
  loading?: boolean;
  error?: boolean;
}

const CHECKLIST_VERSION = '2026-09-26';

function dismissalKey(userId: string | undefined): string {
  return `financial-app:getting-started:${userId ?? 'local'}:${CHECKLIST_VERSION}`;
}

function wasDismissed(userId: string | undefined): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(dismissalKey(userId)) === 'dismissed';
  } catch {
    return false;
  }
}

export function GettingStartedChecklist({
  userId,
  steps,
}: {
  userId?: string;
  steps: GettingStartedStep[];
}) {
  const [dismissed, setDismissed] = useState(() => wasDismissed(userId));
  const completed = steps.filter((step) => step.complete).length;
  const allComplete = steps.length > 0 && completed === steps.length;

  if (dismissed || allComplete) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(dismissalKey(userId), 'dismissed');
    } catch {
      // El almacenamiento puede estar bloqueado; ocultarlo durante esta sesión sigue siendo útil.
    }
    setDismissed(true);
  };

  return (
    <section
      className="mb-4 rounded-[18px] border border-blue-100 bg-blue-50/70 p-4 shadow-sm sm:p-5"
      aria-labelledby="getting-started-title"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[.08em] text-blue-700">
            Primeros pasos
          </p>
          <h2 id="getting-started-title" className="mt-0.5 text-base font-bold text-slate-900">
            Empecemos con lo básico
          </h2>
          <p className="mt-1 text-sm leading-5 text-slate-600" aria-live="polite">
            {completed} de {steps.length} listos. Empieza por los primeros tres.
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="tap-target -mr-2 -mt-2 shrink-0 rounded-xl px-2 text-sm font-semibold text-slate-600 hover:bg-white/80 hover:text-slate-900"
          aria-label="Ocultar checklist de primeros pasos"
        >
          Ocultar
        </button>
      </div>

      <div
        className="mt-3 h-2 overflow-hidden rounded-full bg-blue-100"
        role="progressbar"
        aria-label="Avance de primeros pasos"
        aria-valuemin={0}
        aria-valuemax={steps.length}
        aria-valuenow={completed}
      >
        <span
          className="block h-full rounded-full bg-blue-700 transition-[width]"
          style={{ width: `${(completed / steps.length) * 100}%` }}
        />
      </div>

      <ol className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
        {steps.map((step, index) => (
          <li key={step.id}>
            <Link
              to={step.to}
              aria-label={`${step.error ? 'Sin comprobar' : step.complete ? 'Listo' : 'Pendiente'}: ${step.title}`}
              className="tap-target flex h-full min-h-[76px] items-start gap-2.5 rounded-[14px] border border-blue-100 bg-white p-3 text-left shadow-sm transition hover:border-blue-200 hover:shadow"
            >
              <span
                className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                  step.complete
                    ? 'bg-emerald-100 text-emerald-800'
                    : step.error
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-slate-900 text-white'
                }`}
                aria-hidden
              >
                {step.loading ? '…' : step.error ? '!' : step.complete ? '✓' : index + 1}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold leading-5 text-slate-900">
                  {step.title}
                </span>
                <span className="mt-0.5 block text-xs leading-4 text-slate-600">
                  {step.loading
                    ? 'Comprobando avance…'
                    : step.error
                      ? 'No pudimos comprobar este paso. Puedes abrirlo o recargar.'
                      : step.description}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ol>

      <p className="mt-3 text-xs leading-5 text-slate-600">
        El avance se toma de tus datos registrados. Si ocultas esta lista, la elección se guarda en
        este navegador.
      </p>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white/80 px-3 py-2.5">
        <p className="text-xs leading-5 text-slate-600">
          Después de confirmar tu primer gasto, vuelve a Panorama o haz una pregunta. El análisis
          usa una consulta.
        </p>
        <Link
          to="/asistente"
          className="tap-target inline-flex items-center text-xs font-bold text-blue-700 underline"
        >
          Ir al asistente
        </Link>
      </div>
    </section>
  );
}
